import { schemaPartner } from 'cpool-schemas';
import { z } from 'zod';

// Backend-only columns — deliberately not in schemaPartner, which is the payload schema of every Partner event.
// `locked` comes from schemaPartner as a plain nullish boolean; each discriminated-union variant below
// overrides it with its own literal via `.extend()`, so it doesn't need to be omitted here.
export const PartnersTableBaseZod = schemaPartner.extend({
    id: z.string(),
    // timestamp of the last projected partner event; nullish for pre-field rows
    lastChangedAt: z.number().nullish(),
    // event timestamp of Partner_Locked; nullish for pre-field rows
    lockedAt: z.number().nullish(),
    // normalizePhone(phone) at projection time — same normalization as Chats.phone; nullish for pre-field rows
    phoneE164: z.string().min(1).nullish(),
});

// `locked` discriminates the row shape: true -> only lockedReason may be set, false -> only
// unlockedReason may be set, null (never locked/unlocked) -> neither may exist.
export const PartnersTableDefinitionZod = z.discriminatedUnion('locked', [
    PartnersTableBaseZod.extend({
        locked: z.literal(true),
        // reason from Partner_Locked; nullish for pre-field rows
        lockedReason: z.string().nullish(),
    }),
    PartnersTableBaseZod.extend({
        locked: z.literal(false),
        // reason from Partner_Unlocked; nullish for pre-field rows
        unlockedReason: z.string().nullish(),
    }),
    PartnersTableBaseZod.extend({
        locked: z.literal(null),
    }),
]);

export type PartnersTableSchema = z.infer<typeof PartnersTableDefinitionZod>;

// `keyof` a union only yields the fields common to every branch, so this distributes over the union
// to collect every field that exists on ANY `locked` state instead — needed wherever code must know
// every possible column (UI field grouping, "did we forget a field" test guards, ...).
export type PartnersTableFieldName = PartnersTableSchema extends infer TVariant
    ? TVariant extends object
        ? keyof TVariant
        : never
    : never;

export const PARTNERS_TABLE_FIELD_NAMES: PartnersTableFieldName[] = Array.from(
    new Set(PartnersTableDefinitionZod.options.flatMap((option) => Object.keys(option.shape))),
) as PartnersTableFieldName[];
